import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import { SavedRepliesQuery } from './useSavedRepliesQuery'
import type { SavedReplyId } from '../../types'

// The codegen `SavedRepliesQueryQuery` masks the query's `@_unmask` fragments
// behind `$fragmentRefs`, hiding the scalar fields this cache update reads and
// writes; deriving the result via `ResultOf` inlines them.
type SavedRepliesQueryQuery = ResultOf<typeof SavedRepliesQuery>

type SavedReply = Extract<
  ResultOf<typeof UpdateSavedReply>['updateSavedReply'],
  { savedReply: unknown }
>['savedReply']

export type UpdateSavedReply = (params: {
  id: SavedReplyId
  title?: string | null
  content?: string
  hotkeys?: string[] | null
}) => Promise<SavedReply>

export const UpdateSavedReply = graphql(/* GraphQL */ `
  mutation updateSavedReply($input: UpdateSavedReplyInput!) {
    updateSavedReply(input: $input) {
      ... on SavedReplyActionSuccess {
        savedReply {
          id
          organizationId
          title
          content {
            text
          }
          hotkeys
          createdAt
          updatedAt
        }
      }
      ... on MutationError {
        message
      }
    }
  }
`)

/**
 * Hook to update a saved reply.
 * @returns {UpdateSavedReply} Function to update a saved reply.
 * @example
 * const updateSavedReply = useUpdateSavedReply();
 *
 * const handleUpdateSavedReply = async () => {
 *   try {
 *     const updatedSavedReply = await updateSavedReply({ id: '123', title: 'New Saved Reply', content: 'This is a new saved reply' });
 *
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 */
export function useUpdateSavedReply(): UpdateSavedReply {
  const [updateSavedReplyMutation] = useMutation(UpdateSavedReply, {
    update(cache, { data }) {
      if (
        !data?.updateSavedReply ||
        data.updateSavedReply.__typename !== 'SavedReplyActionSuccess'
      )
        return

      const updatedItem = data.updateSavedReply.savedReply

      const variables = {
        input: { organizationId: updatedItem.organizationId },
      }

      // Read from the cache with correct variables
      const existingData = cache.readQuery<SavedRepliesQueryQuery>({
        query: SavedRepliesQuery,
        variables,
      })

      if (!existingData) return

      const updatedSavedReplies = existingData.savedReplies.savedReplies.map(
        (savedReply) =>
          savedReply.id === updatedItem.id ? updatedItem : savedReply,
      )

      // `__typename` is required by Apollo at write time but isn't part of the
      // query's selection set (so it's absent from the derived type). Building
      // the payload as a separate object keeps it out of the excess-property
      // check while preserving the runtime shape Apollo needs.
      const updatedData = {
        savedReplies: {
          __typename: 'SavedRepliesResponse',
          savedReplies: updatedSavedReplies,
        },
      }

      cache.writeQuery({
        query: SavedRepliesQuery,
        variables,
        data: updatedData,
      })
    },
  })

  const updateSavedReply = useCallback(
    async ({
      id,
      title,
      content,
      hotkeys,
    }: {
      id: SavedReplyId
      title?: string | null
      content?: string
      hotkeys?: string[] | null
    }): Promise<SavedReply> => {
      /* undefined values keep the content with the same value */
      const dataToUpdate = {
        ...(title !== undefined && { title: title === '' ? null : title }),
        ...(hotkeys !== undefined && { hotkeys }),
        ...(content !== undefined && { content: { text: content } }),
      }

      const { data, error } = await updateSavedReplyMutation({
        variables: {
          input: {
            id,
            savedReply: {
              ...dataToUpdate,
            },
          },
        },
      })

      if (error || !data) {
        throw new Error(error?.message ?? 'Failed to update saved reply')
      }

      const result = data.updateSavedReply
      if (result.__typename !== 'SavedReplyActionSuccess') {
        throw new Error(
          'message' in result && typeof result.message === 'string'
            ? result.message
            : 'Failed to update saved reply',
        )
      }

      return result.savedReply
    },
    [updateSavedReplyMutation],
  )

  return updateSavedReply
}
